import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import api from '../../api';
import { ArrowLeft, Check, Clock, X, Banknote, Smartphone, Pencil, Camera, Plus, Trash2 } from 'lucide-react';

const DetailRow = ({ label, value, valueStyle }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ fontSize: '13px', color: 'var(--text2)', flexShrink: 0, marginRight: 12 }}>{label}</span>
    <span style={{ fontSize: '14px', fontWeight: 600, textAlign: 'right', ...valueStyle }}>{value}</span>
  </div>
);

export default function SalesList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedSale, setSelectedSale] = useState(null);
  const [editSale, setEditSale] = useState(null);
  const [editForm, setEditForm] = useState({ payment_method: '', is_paid: null });
  const [saving, setSaving] = useState(false);
  // Custom work edit state
  const [cwEdit, setCwEdit] = useState(null); // custom work detail
  const [cwForm, setCwForm] = useState({ description: '', client_id: '', price: '', cost: '', photos: [], payment_method: '', is_paid: null });
  const [cwSaving, setCwSaving] = useState(false);
  const cwFileRef = useRef(null);
  
  const urlPeriod = searchParams.get('period');
  const urlFromDate = searchParams.get('fromDate');
  const urlToDate = searchParams.get('toDate');
  const [period, setPeriod] = useState(urlPeriod || 'daily');
  const [fromDate, setFromDate] = useState(urlFromDate || '');
  const [toDate, setToDate] = useState(urlToDate || '');

  useEffect(() => { load(); }, [period, fromDate, toDate, user]);
  useEffect(() => {
    if (user?.id) api.get(`/clients/${user.id}`).then(r => setClients(r.data)).catch(() => {});
  }, [user?.id]);

  const parseUTC = (iso) => {
    if (!iso) return null;
    if (!iso.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(iso)) {
      return new Date(iso.replace(' ', 'T') + 'Z');
    }
    return new Date(iso);
  };

  const load = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      let url = `/analytics/revenue/${user.id}?period=${period}`;
      if (period === 'range' && fromDate && toDate) {
        url = `/analytics/revenue/${user.id}?from_date=${fromDate}&to_date=${toDate}`;
      }
      const r = await api.get(url);
      const startDate = r.data.start_date;
      const endDate = r.data.end_date;
      const salesRes = await api.get(`/sales/${user.id}`);
      let allSales = salesRes.data || [];
      if (startDate && endDate) {
        const start = parseUTC(startDate);
        const end = parseUTC(endDate);
        allSales = allSales.filter(s => {
          if (!s.created_at) return false;
          const saleDate = parseUTC(s.created_at);
          return saleDate >= start && saleDate < end;
        });
      }
      allSales.sort((a, b) => parseUTC(b.created_at) - parseUTC(a.created_at));
      setSales(allSales);
    } catch (e) { console.error('Error cargando ventas:', e); }
    finally { setLoading(false); }
  };

  const filtered = sales.filter(s => {
    if (filter === 'paid') return s.is_paid;
    if (filter === 'unpaid') return !s.is_paid;
    return true;
  });

  const fmtDate = (iso) => {
    if (!iso) return '-';
    const d = parseUTC(iso);
    return d.toLocaleString('es-AR', {
      timeZone: 'America/Argentina/Buenos_Aires',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  };

  const fmt = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  const fmtPayment = (method) => method === 'efectivo' ? 'Efectivo' : method === 'transferencia' ? 'Transferencia' : (method || '-');
  const periodLabels = { daily: 'Hoy', weekly: 'Semana', monthly: 'Mes', yearly: 'Año', range: 'Rango' };

  const openEdit = (sale) => {
    setSelectedSale(null);
    if (sale.is_custom_work && sale.custom_work_id) {
      // Fetch custom work detail for full edit
      api.get(`/custom-work/id/${sale.custom_work_id}`)
        .then(r => {
          const cw = r.data;
          setCwEdit({ sale, cw });
          setCwForm({
            description: cw.description || '',
            client_id: cw.client_id || '',
            price: String(cw.price),
            cost: String(cw.cost),
            photos: cw.photos || [],
            payment_method: cw.payment_method || '',
            is_paid: cw.is_paid,
          });
        })
        .catch(() => alert('Error al cargar el trabajo personalizado'));
    } else {
      setEditSale(sale);
      setEditForm({ payment_method: sale.payment_method || '', is_paid: sale.is_paid });
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (editForm.payment_method) payload.payment_method = editForm.payment_method;
      if (editForm.is_paid !== null) payload.is_paid = editForm.is_paid;
      await api.put(`/sales/${editSale.id}`, payload);
      setEditSale(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al editar venta');
    } finally { setSaving(false); }
  };

  const addCwPhoto = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => setCwForm(f => ({ ...f, photos: [...f.photos, ev.target.result] }));
      reader.readAsDataURL(file);
    });
  };

  const saveCwEdit = async () => {
    if (!cwForm.price || !cwForm.client_id) { alert('Precio y cliente son obligatorios'); return; }
    setCwSaving(true);
    try {
      await api.put(`/custom-work/${cwEdit.cw.id}`, {
        description: cwForm.description || null,
        client_id: cwForm.client_id,
        price: parseFloat(cwForm.price),
        cost: parseFloat(cwForm.cost) || 0,
        photos: cwForm.photos,
        payment_method: cwForm.payment_method || null,
        is_paid: cwForm.is_paid,
      });
      setCwEdit(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al editar trabajo');
    } finally { setCwSaving(false); }
  };

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" onClick={() => navigate(-1)}><ArrowLeft size={24} strokeWidth={1.5} /></button>
        <h1 className="header-title" style={{ flex: 1 }}>Listado de Ventas</h1>
        <span className="card-row-detail">{filtered.length}</span>
      </div>

      <div className="period-tabs" style={{ padding: '0 16px', marginBottom: '8px' }}>
        {['daily', 'weekly', 'monthly', 'yearly', 'range'].map(p => (
          <button key={p} className={`period-tab ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>
            {periodLabels[p]}
          </button>
        ))}
      </div>

      {period === 'range' && (
        <div style={{ display: 'flex', gap: '8px', padding: '0 16px', marginBottom: '8px' }}>
          <input type="date" className="input" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ flex: 1 }} />
          <input type="date" className="input" value={toDate} onChange={e => setToDate(e.target.value)} style={{ flex: 1 }} />
        </div>
      )}

      <div className="filter-tabs">
        {[['all','Todos'],['paid','Cobrados'],['unpaid','Pendientes']].map(([k,l]) => (
          <button key={k} className={`filter-tab ${filter === k ? 'active' : ''}`} onClick={() => setFilter(k)}>{l}</button>
        ))}
      </div>

      <div className="content-scroll">
        {loading ? <div className="spinner" style={{ marginTop: '40px' }} /> : (
          <>
            {filtered.map(s => (
              <div
                key={s.id}
                className="card-row"
                data-testid={`sale-item-${s.id}`}
                style={{ gap: '12px', cursor: 'pointer' }}
                onClick={() => setSelectedSale(s)}
              >
                <div className={`sale-icon ${s.is_paid ? 'paid' : 'unpaid'}`}>
                  {s.is_paid ? <Check size={20} /> : <Clock size={20} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600' }}>{s.product_name || 'Sin nombre'}</div>
                  <div className="card-row-detail">{fmtDate(s.created_at)}</div>
                  {s.client_name && <div className="card-row-detail">{s.client_name}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '17px', fontWeight: '700' }}>{fmt(s.amount)}</div>
                  {s.is_paid && s.payment_method && (
                    <span style={{ fontSize: '12px', color: 'var(--text2)' }}>{fmtPayment(s.payment_method)}</span>
                  )}
                  {!s.is_paid && <span className="unpaid-badge">Pendiente</span>}
                  <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: 2 }}>Ver detalle ›</div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="empty-state">
                <span className="empty-icon"><Clock size={48} strokeWidth={1} /></span>
                <p className="empty-text">No hay ventas en este período</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Sale Detail Modal ──────────────────────────────── */}
      {selectedSale && (
        <div className="modal-overlay" onClick={() => setSelectedSale(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ margin: 0 }}>Detalle de Venta</h3>
              <button onClick={() => setSelectedSale(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)' }}>
                <X size={22} />
              </button>
            </div>

            {/* Status badge */}
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 16px', borderRadius: 20,
                background: selectedSale.is_paid ? 'rgba(76,175,80,0.15)' : 'rgba(255,107,107,0.15)',
                color: selectedSale.is_paid ? '#4CAF50' : '#ff6b6b',
                fontWeight: 700, fontSize: '14px',
              }}>
                {selectedSale.is_paid ? <Check size={16} /> : <Clock size={16} />}
                {selectedSale.is_paid ? 'Cobrado' : 'Pendiente de cobro'}
              </span>
            </div>

            {/* Amount hero */}
            <div style={{ textAlign: 'center', marginBottom: 20, padding: '14px', background: 'var(--surface)', borderRadius: 12 }}>
              <div style={{ fontSize: '11px', color: 'var(--text2)', marginBottom: 4 }}>Monto</div>
              <div style={{ fontSize: '34px', fontWeight: 800 }}>{fmt(selectedSale.amount)}</div>
              {selectedSale.profit > 0 && (
                <div style={{ fontSize: '13px', color: '#4ECDC4', marginTop: 4 }}>
                  Ganancia: {fmt(selectedSale.profit)}
                </div>
              )}
            </div>

            <DetailRow label="Producto / Servicio" value={selectedSale.product_name || '-'} />
            <DetailRow
              label="Fecha y hora"
              value={fmtDate(selectedSale.created_at)}
            />
            <DetailRow
              label="Cliente"
              value={selectedSale.client_name || 'Sin cliente asignado'}
              valueStyle={!selectedSale.client_name ? { color: 'var(--text2)', fontWeight: 400, fontStyle: 'italic' } : {}}
            />
            <DetailRow
              label="Forma de pago"
              value={selectedSale.is_paid ? fmtPayment(selectedSale.payment_method) : 'Aún no cobrado'}
              valueStyle={!selectedSale.is_paid ? { color: 'var(--text2)', fontWeight: 400 } : {}}
            />
            <DetailRow
              label="Estado"
              value={selectedSale.is_paid ? 'Cobrado' : 'Pendiente'}
              valueStyle={{ color: selectedSale.is_paid ? '#4CAF50' : '#ff6b6b' }}
            />

            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setSelectedSale(null)}>
                Cerrar
              </button>
              <button
                data-testid={`edit-sale-btn-${selectedSale.id}`}
                className="btn-primary"
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                onClick={() => openEdit(selectedSale)}
              >
                <Pencil size={14} /> Editar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Edit Sale Modal ── */}
      {editSale && (
        <div className="modal-overlay" onClick={() => setEditSale(null)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ margin: 0 }}>Editar Venta</h3>
              <button onClick={() => setEditSale(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)' }}>
                <X size={22} />
              </button>
            </div>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: 16, color: 'var(--text2)' }}>{editSale.product_name}</div>

            <div className="form-group">
              <label className="label">Estado</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" data-testid="edit-sale-paid"
                  onClick={() => setEditForm(f => ({ ...f, is_paid: true }))}
                  style={{ flex: 1, padding: 10, borderRadius: 10, cursor: 'pointer', border: `2px solid ${editForm.is_paid ? '#4CAF50' : 'var(--border)'}`, background: editForm.is_paid ? 'rgba(76,175,80,0.12)' : 'var(--surface)', color: editForm.is_paid ? '#4CAF50' : 'var(--text2)', fontWeight: editForm.is_paid ? 700 : 400 }}>
                  <Check size={14} style={{ marginRight: 4 }} /> Cobrado
                </button>
                <button type="button" data-testid="edit-sale-unpaid"
                  onClick={() => setEditForm(f => ({ ...f, is_paid: false, payment_method: '' }))}
                  style={{ flex: 1, padding: 10, borderRadius: 10, cursor: 'pointer', border: `2px solid ${editForm.is_paid === false ? '#FF9800' : 'var(--border)'}`, background: editForm.is_paid === false ? 'rgba(255,152,0,0.12)' : 'var(--surface)', color: editForm.is_paid === false ? '#FF9800' : 'var(--text2)', fontWeight: editForm.is_paid === false ? 700 : 400 }}>
                  <Clock size={14} style={{ marginRight: 4 }} /> Pendiente
                </button>
              </div>
            </div>

            {editForm.is_paid && (
              <div className="form-group">
                <label className="label">Forma de pago</label>
                <div className="payment-row">
                  <button type="button" data-testid="edit-payment-efectivo"
                    className={`payment-btn ${editForm.payment_method === 'efectivo' ? 'active' : ''}`}
                    onClick={() => setEditForm(f => ({ ...f, payment_method: 'efectivo' }))}>
                    <Banknote size={16} /> Efectivo
                  </button>
                  <button type="button" data-testid="edit-payment-transferencia"
                    className={`payment-btn ${editForm.payment_method === 'transferencia' ? 'active' : ''}`}
                    onClick={() => setEditForm(f => ({ ...f, payment_method: 'transferencia' }))}>
                    <Smartphone size={16} /> Transferencia
                  </button>
                </div>
              </div>
            )}

            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setEditSale(null)}>Cancelar</button>
              <button data-testid="confirm-edit-sale-btn" className="btn-primary" onClick={saveEdit} disabled={saving} style={{ flex: 1 }}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Custom Work Modal ── */}
      {cwEdit && (
        <div className="modal-overlay" onClick={() => setCwEdit(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ margin: 0 }}>Editar Trabajo Personalizado</h3>
              <button onClick={() => setCwEdit(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)' }}>
                <X size={22} />
              </button>
            </div>

            <div className="form-group">
              <label className="label">Descripción</label>
              <textarea className="input" rows={2} value={cwForm.description} onChange={e => setCwForm(f => ({ ...f, description: e.target.value }))} placeholder="Descripción del trabajo..." />
            </div>

            <div className="form-group">
              <label className="label">Cliente *</label>
              <select className="input" value={cwForm.client_id} onChange={e => setCwForm(f => ({ ...f, client_id: e.target.value }))}>
                <option value="">Seleccionar cliente</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="label">Precio *</label>
                <input className="input" type="number" step="0.01" value={cwForm.price} onChange={e => setCwForm(f => ({ ...f, price: e.target.value }))} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="label">Costo</label>
                <input className="input" type="number" step="0.01" value={cwForm.cost} onChange={e => setCwForm(f => ({ ...f, cost: e.target.value }))} />
              </div>
            </div>

            <div className="form-group">
              <label className="label">Estado</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => setCwForm(f => ({ ...f, is_paid: true }))}
                  style={{ flex: 1, padding: 10, borderRadius: 10, cursor: 'pointer', border: `2px solid ${cwForm.is_paid ? '#4CAF50' : 'var(--border)'}`, background: cwForm.is_paid ? 'rgba(76,175,80,0.12)' : 'var(--surface)', color: cwForm.is_paid ? '#4CAF50' : 'var(--text2)', fontWeight: cwForm.is_paid ? 700 : 400 }}>
                  <Check size={14} style={{ marginRight: 4 }} /> Cobrado
                </button>
                <button type="button" onClick={() => setCwForm(f => ({ ...f, is_paid: false, payment_method: '' }))}
                  style={{ flex: 1, padding: 10, borderRadius: 10, cursor: 'pointer', border: `2px solid ${cwForm.is_paid === false ? '#FF9800' : 'var(--border)'}`, background: cwForm.is_paid === false ? 'rgba(255,152,0,0.12)' : 'var(--surface)', color: cwForm.is_paid === false ? '#FF9800' : 'var(--text2)', fontWeight: cwForm.is_paid === false ? 700 : 400 }}>
                  <Clock size={14} style={{ marginRight: 4 }} /> Pendiente
                </button>
              </div>
            </div>

            {cwForm.is_paid && (
              <div className="form-group">
                <label className="label">Forma de pago</label>
                <div className="payment-row">
                  <button type="button" className={`payment-btn ${cwForm.payment_method === 'efectivo' ? 'active' : ''}`} onClick={() => setCwForm(f => ({ ...f, payment_method: 'efectivo' }))}>
                    <Banknote size={16} /> Efectivo
                  </button>
                  <button type="button" className={`payment-btn ${cwForm.payment_method === 'transferencia' ? 'active' : ''}`} onClick={() => setCwForm(f => ({ ...f, payment_method: 'transferencia' }))}>
                    <Smartphone size={16} /> Transferencia
                  </button>
                </div>
              </div>
            )}

            {/* Photos */}
            <div className="form-group">
              <label className="label"><Camera size={14} style={{ marginRight: 6 }} /> Fotos del trabajo</label>
              <input type="file" ref={cwFileRef} accept="image/*" multiple onChange={addCwPhoto} style={{ display: 'none' }} />
              <button type="button" className="btn-secondary" onClick={() => cwFileRef.current?.click()} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                <Plus size={14} /> Agregar fotos
              </button>
              {cwForm.photos.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {cwForm.photos.map((p, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={p} alt="" style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 8 }} />
                      <button onClick={() => setCwForm(f => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }))}
                        style={{ position: 'absolute', top: -6, right: -6, background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setCwEdit(null)}>Cancelar</button>
              <button data-testid="save-cw-edit-btn" className="btn-primary" onClick={saveCwEdit} disabled={cwSaving} style={{ flex: 1 }}>
                {cwSaving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}